import {
	FormControl,
	Checkbox,
	InputLabel,
	ListItemText,
	MenuItem,
	OutlinedInput,
	Select,
	type SelectChangeEvent,
} from "@mui/material";
import type { ReactNode } from "react";
import { useCategoriesList } from "../hook/useCategoriesList";
import _ from "lodash";
import type { ISubCategory } from "../../entities/category.entity";



const generateSubCategoryPaths = (
	subCategory: ISubCategory & { categoryName: string },
): string[] => {
	const fullPath = `${subCategory.categoryName} / ${subCategory.name}`;
	
	if (!subCategory.name.includes("/")) {
		return [fullPath];
	}

	const paths: string[] = [];
	const parts = subCategory.name.split("/").map(part => part.trim());
	
	// Generate hierarchical paths
	for (let i = 0; i < parts.length; i++) {
		const pathParts = parts.slice(0, i + 1);
		const pathName = pathParts.join(" / ");
		
		// Skip empty parts (handles cases like "Parent/ - ")
		if (pathName && !pathName.endsWith(" / -") && !pathName.endsWith("/ -")) {
			paths.push(`${subCategory.categoryName} / ${pathName}`);
		}
	}
	
	return paths;
};

export function SubCategoryPathInput({
	value = [],
	onChange,
}: {
	value: string[];
	onChange: (event: SelectChangeEvent<string[]>, child: ReactNode) => void;
}) {
	const categoriesList = useCategoriesList();

	const subCategories =
		_.chain(categoriesList.data)
			.map((category) => {
				return _.chain(category.subCategories)
					.map((subCategory) => {
						return {
							...subCategory,
							categoryName: category.name,
						};
					})
					.value();
			})
			.flatten()
			.value() || [];

	// Generate all possible paths from subcategories
	const allPaths = _.chain(subCategories)
		.flatMap((subCategory) => generateSubCategoryPaths(subCategory))
		.uniq()
		.sort()
		.value();

	if (categoriesList.isLoading) return <div>Loading...</div>;

	return (
		<FormControl fullWidth>
			<InputLabel id="subCategoryPath-input-label">Caminhos de Categorias</InputLabel>
			<Select
				labelId="subCategoryPath-input-label"
				multiple
				value={value}
				name="subCategoryPaths"
				required
				onChange={onChange}
				input={<OutlinedInput label="Caminhos de Categorias" />}
				renderValue={(selected) => {
					const selectedPaths = selected as string[];
					return selectedPaths.join(", ");
				}}
			>
				{allPaths.map((path) => (
					<MenuItem key={path} value={path}>
						<Checkbox checked={value.includes(path)} />
						<ListItemText primary={path} />
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}
